import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemIcon,
    ListItemText,
    makeStyles,
    Paper,
    Popover,
    TextField,
    Typography,
    Avatar,
    Chip,
} from "@material-ui/core"
import { Chat, GetApp, InsertEmoticon, Refresh, Send } from "@material-ui/icons"
import { useState, useEffect, useRef } from "react"
import { useSnackbar } from "notistack"
import clsx from "clsx"
import api from "../../services/api"
import { getRestApiBaseUrl } from "../../utils/restApiBaseUrl"
import useAppStore from "../../stores/AppStore"
import Cabecalho from "../Cabecalho"

const drawerWidth = 250
const miniDrawerWidth = 80

const useStyles = makeStyles(theme => ({
    root: {
        height: "100vh",
        backgroundColor: "#f5f5f5",
        paddingTop: theme.spacing(10),
        padding: theme.spacing(2),
        display: "flex",
        flexDirection: "column",
        marginLeft: miniDrawerWidth,
        width: `calc(100% - ${miniDrawerWidth}px)`,
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflow: "hidden",
        [theme.breakpoints.down("md")]: {
            marginLeft: 0,
            width: "100%",
        },
    },
    rootShift: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["margin", "width"], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        [theme.breakpoints.down("md")]: {
            marginLeft: 0,
            width: "100%",
        },
    },
    container: {
        flex: 1,
        display: "flex",
        gap: theme.spacing(2),
        height: "calc(100vh - 180px)",
        minHeight: 0,
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            height: "calc(100vh - 200px)",
        },
    },
    header: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
    },
    conversationsPanel: {
        width: "35%",
        minWidth: 300,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[2],
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "40%",
        },
    },
    conversationPanelHeader: {
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: "#f0f2f5",
        flexShrink: 0, // Garante que não encolha
    },
    conversationsListContainer: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        minHeight: 0, // Importante para o flex funcionar corretamente
    },
    title: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    conversationItem: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:hover": {
            backgroundColor: "#f5f5f5",
            cursor: "pointer",
        },
        "&:last-child": {
            borderBottom: "none",
        },
        "&.selected": {
            backgroundColor: "#e9edef",
        },
    },
    avatar: {
        backgroundColor: "#25D366",
    },
    messagePreview: {
        color: theme.palette.text.secondary,
        fontSize: "0.875rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    timestamp: {
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
    },
    unreadBadge: {
        backgroundColor: "#25D366",
        color: "#fff",
        fontWeight: "bold",
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(8),
        color: theme.palette.text.secondary,
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px",
    },
    chatPanel: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[2],
        overflow: "hidden",
        height: "100%",
        maxHeight: "calc(100vh - 180px)",
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "60%",
            maxHeight: "60vh",
        },
    },
    chatHeader: {
        padding: theme.spacing(1.5, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: "#f0f2f5",
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        flexShrink: 0, // Garante que não encolha
    },
    chatHeaderTop: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    chatHeaderInfo: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    chatHeaderDetails: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
    },
    automaticaButton: {
        textTransform: "none",
        fontSize: "0.875rem",
    },
    automaticaButtonEnabled: {
        backgroundColor: "#64B5F6", // Azul claro
        color: "#fff",
        "&:hover": {
            backgroundColor: "#42A5F5",
        },
    },
    automaticaButtonDisabled: {
        backgroundColor: "#EF5350", // Vermelho
        color: "#fff",
        "&:hover": {
            backgroundColor: "#E53935",
        },
    },
    messagesArea: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        padding: theme.spacing(2),
        backgroundColor: "#e5ddd5",
        backgroundImage: "url('data:image/svg+xml,%3Csvg width=\\'100\\' height=\\'100\\' xmlns=\\'http://www.w3.org/2000/svg\\'%3E%3Cdefs%3E%3Cpattern id=\\'grid\\' width=\\'100\\' height=\\'100\\' patternUnits=\\'userSpaceOnUse\\'%3E%3Cpath d=\\'M 100 0 L 0 0 0 100\\' fill=\\'none\\' stroke=\\'%23e5ddd5\\' stroke-width=\\'1\\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\\'100\\' height=\\'100\\' fill=\\'url(%23grid)\\'/%3E%3C/svg%3E')",
        minHeight: 0,
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
    },
    messageBubble: {
        maxWidth: "75%",
        padding: theme.spacing(1.5, 2),
        borderRadius: 8,
        marginBottom: theme.spacing(1),
        wordBreak: "break-word",
        display: "flex",
        flexDirection: "column",
    },
    messageText: {
        marginBottom: theme.spacing(0.5),
    },
    messageTime: {
        fontSize: "0.6875rem",
        color: "rgba(0, 0, 0, 0.45)",
        alignSelf: "flex-end",
        marginTop: theme.spacing(0.5),
    },
    messageIn: {
        alignSelf: "flex-start",
        backgroundColor: "#fff",
        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    },
    messageOut: {
        alignSelf: "flex-end",
        backgroundColor: "#d9fdd3",
        marginLeft: "auto",
        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
    },
    sendArea: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: "#f0f2f5",
        flexShrink: 0, // Garante que não encolha
    },
    emptyChatState: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        color: theme.palette.text.secondary,
        padding: theme.spacing(4),
    },
    sendInput: {
        flex: 1,
        "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
        },
    },
    headerActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginTop: theme.spacing(1),
    },
    refreshButton: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    contador: {
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
        fontWeight: 500,
    },
}))

const MENSAGEM_ERRO_24H =
    "Não há permissão para responder a mensagem porque o último envio do cliente foi a mais de 24 horas."

const EMOJIS_COMUNS = [
    "😀", "😃", "😄", "😁", "😅", "😂", "🤣", "😊", "😇", "🙂",
    "😉", "😍", "🥰", "😘", "😗", "😋", "😛", "😜", "🤗", "🤔",
    "👍", "👎", "👏", "🙌", "🤝", "🙏", "❤️", "🧡", "💛", "💚",
    "💙", "💜", "🖤", "🤍", "💕", "💯", "✅", "❌", "⭐", "🔥",
]

export default function ConversasWhatsapp() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const configuracoes = useAppStore(s => s.configuracoes)
    const [conversas, setConversas] = useState([])
    const [loading, setLoading] = useState(true)
    const [conversaSelecionada, setConversaSelecionada] = useState(null)
    const [textoMensagem, setTextoMensagem] = useState("")
    const [enviando, setEnviando] = useState(false)
    const [carregandoMensagens, setCarregandoMensagens] = useState(false)
    const [tempoRestante, setTempoRestante] = useState(60)
    const [atualizando, setAtualizando] = useState(false)
    const messagesAreaRef = useRef(null)
    const [conversasSelecionadas, setConversasSelecionadas] = useState([])
    const [openDisparoMassa, setOpenDisparoMassa] = useState(false)
    const [textoDisparoMassa, setTextoDisparoMassa] = useState("")
    const [enviandoMassa, setEnviandoMassa] = useState(false)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const conversationsListRef = useRef(null)
    const conversasRef = useRef([])
    const [detalhesConversa, setDetalhesConversa] = useState(null)
    const [carregandoDetalhes, setCarregandoDetalhes] = useState(false)
    const [emojiPickerAnchor, setEmojiPickerAnchor] = useState(null)
    const wsRef = useRef(null)
    const [wsConnected, setWsConnected] = useState(false)

    const formatarTimestamp = dataEnvio => {
        if (!dataEnvio || dataEnvio === 0) return "Agora"

        const data = new Date(dataEnvio)
        const agora = new Date()
        const diffMs = agora - data
        const diffMin = Math.floor(diffMs / 60000)
        const diffHoras = Math.floor(diffMin / 60)
        const diffDias = Math.floor(diffHoras / 24)

        if (diffMin < 1) return "Agora"
        if (diffMin < 60) return `${diffMin}m`
        if (diffHoras < 24) return `${diffHoras}h`
        if (diffDias === 1) return "Ontem"
        if (diffDias < 7) return `${diffDias}d`
        return data.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })
    }

    const conversaTemMensagemUltimas24h = conversa => {
        if (!conversa.mensagens || conversa.mensagens.length === 0) {
            // Fallback: se a conversa tem ultimaDataEnvio nas últimas 24h (ex.: lista já mostra "agora"), considera dentro da janela
            const ultima = conversa.ultimaDataEnvio || 0
            if (ultima > 0) {
                const agora = new Date().getTime()
                const vinteQuatroHorasAtras = agora - 24 * 60 * 60 * 1000
                const ts = ultima < 1e12 ? ultima * 1000 : ultima // aceita segundos ou ms
                if (ts >= vinteQuatroHorasAtras) return true
            }
            return false
        }

        const agora = new Date().getTime()
        const vinteQuatroHorasAtras = agora - 24 * 60 * 60 * 1000

        // Verifica se há alguma mensagem (IN ou OUT) das últimas 24 horas, alinhado ao indicador "agora" / "2m" etc.
        return conversa.mensagens.some(msg => {
            let dataEnvio = msg.dataEnvio || 0
            if (dataEnvio === 0) return true
            if (dataEnvio < 1e12) dataEnvio = dataEnvio * 1000 // API em segundos
            return dataEnvio >= vinteQuatroHorasAtras
        })
    }

    const toggleSelecaoConversa = telefone => {
        setConversasSelecionadas(prev => {
            if (prev.includes(telefone)) {
                return prev.filter(t => t !== telefone)
            } else {
                return [...prev, telefone]
            }
        })
    }

    const handleExportarContatos = () => {
        if (conversas.length === 0) {
            enqueueSnackbar("Não há conversas para exportar", { variant: "warning" })
            return
        }

        try {
            // Criar cabeçalho do CSV
            const cabecalho = "Nome,Telefone\n"

            // Criar linhas do CSV
            const linhas = conversas.map(conversa => {
                const nome = conversa.nome || formatarTelefone(conversa.telefone) || ""
                const telefone = conversa.telefone || ""
                // Escapar vírgulas e aspas no nome
                const nomeEscapado = nome.includes(",") || nome.includes('"')
                    ? `"${nome.replace(/"/g, '""')}"`
                    : nome
                return `${nomeEscapado},${telefone}`
            })

            // Combinar cabeçalho e linhas
            const conteudoCSV = cabecalho + linhas.join("\n")

            // Criar blob e fazer download
            const blob = new Blob(["\uFEFF" + conteudoCSV], {
                type: "text/csv;charset=utf-8;",
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement("a")
            link.href = url

            // Nome do arquivo com data atual
            const dataAtual = new Date().toISOString().split("T")[0]
            link.download = `contatos_whatsapp_${dataAtual}.csv`

            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            enqueueSnackbar(
                `${conversas.length} ${conversas.length === 1 ? "contato exportado" : "contatos exportados"} com sucesso`,
                { variant: "success" }
            )
        } catch (error) {
            console.error("Erro ao exportar contatos:", error)
            enqueueSnackbar("Erro ao exportar contatos", { variant: "error" })
        }
    }

    const handleAbrirDisparoMassa = () => {
        if (conversasSelecionadas.length === 0) {
            enqueueSnackbar("Selecione pelo menos uma conversa", { variant: "warning" })
            return
        }
        setOpenDisparoMassa(true)
    }

    const handleEnviarDisparoMassa = async () => {
        const texto = textoDisparoMassa.trim()
        if (!texto) {
            enqueueSnackbar("Digite uma mensagem para enviar", { variant: "warning" })
            return
        }

        const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id
        const autorizacao = localStorage.getItem("chaveSistema")

        if (!idEstabelecimento || !autorizacao) {
            enqueueSnackbar("Não foi possível enviar. Faça login novamente.", {
                variant: "error",
            })
            return
        }

        setEnviandoMassa(true)
        let sucessos = 0
        let erros = 0

        try {
            for (const telefone of conversasSelecionadas) {
                try {
                    await api.post(
                        `meta/mensagem/${idEstabelecimento}`,
                        { contato: telefone, texto },
                        {
                            headers: {
                                Authorization: autorizacao,
                                "Content-Type": "application/json",
                            },
                        }
                    )
                    sucessos++
                } catch (error) {
                    erros++
                    console.error(`Erro ao enviar para ${telefone}:`, error)
                }
            }

            if (sucessos > 0) {
                enqueueSnackbar(
                    `Mensagem enviada para ${sucessos} ${sucessos === 1 ? "conversa" : "conversas"}${
                        erros > 0 ? `. ${erros} ${erros === 1 ? "erro" : "erros"}` : ""
                    }`,
                    { variant: sucessos > 0 && erros === 0 ? "success" : "warning" }
                )
            } else {
                enqueueSnackbar("Nenhuma mensagem foi enviada", { variant: "error" })
            }

            // Limpar seleções e fechar modal
            setConversasSelecionadas([])
            setTextoDisparoMassa("")
            setOpenDisparoMassa(false)

            // Atualizar lista de conversas (resetar para primeira página)
            setPage(1)
            setHasMore(true)
            await buscarConversas(false, 1, false)
        } catch (error) {
            enqueueSnackbar("Erro ao enviar mensagens em massa", { variant: "error" })
        } finally {
            setEnviandoMassa(false)
        }
    }

    const formatarDataMensagem = dataEnvio => {
        if (!dataEnvio || dataEnvio === 0) {
            const agora = new Date()
            return agora.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
        }

        const data = new Date(dataEnvio)
        const agora = new Date()
        const diffMs = agora - data
        const diffDias = Math.floor(diffMs / (1000 * 60 * 60 * 24))

        // Se for hoje, mostra apenas a hora
        if (diffDias === 0) {
            return data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
        }

        // Se for ontem
        if (diffDias === 1) {
            return `Ontem ${data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
        }

        // Se for da mesma semana
        if (diffDias < 7) {
            const diasSemana = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"]
            return `${diasSemana[data.getDay()]} ${data.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`
        }

        // Mais de uma semana, mostra data completa
        return data.toLocaleString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    const formatarTelefone = telefone => {
        if (!telefone) return ""
        // Remove caracteres não numéricos
        const numeros = telefone.replace(/\D/g, "")
        // Formata: (XX) XXXXX-XXXX
        if (numeros.length === 11) {
            return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7)}`
        }
        return telefone
    }

    const buscarDetalhesConversa = async (contato) => {
        if (!contato) return null

        const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id
        const autorizacao = localStorage.getItem("chaveSistema")

        if (!idEstabelecimento || !autorizacao) {
            return null
        }

        try {
            setCarregandoDetalhes(true)
            const response = await api.get(`meta/conversa/${idEstabelecimento}/${contato}`, {
                headers: {
                    Authorization: autorizacao,
                    "Content-Type": "application/json",
                },
            })

            if (response.data) {
                return response.data
            }
            return null
        } catch (error) {
            console.error("Erro ao buscar detalhes da conversa:", error)
            return null
        } finally {
            setCarregandoDetalhes(false)
        }
    }

    const toggleMensagensAutomaticas = async () => {
        if (!detalhesConversa) return

        const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id
        const autorizacao = localStorage.getItem("chaveSistema")

        if (!idEstabelecimento || !autorizacao) {
            enqueueSnackbar("Não foi possível atualizar. Faça login novamente.", {
                variant: "error",
            })
            return
        }

        try {
            const dadosAtualizados = {
                ...detalhesConversa,
                automatica: !detalhesConversa.automatica,
            }

            await api.post(`meta/conversa/${idEstabelecimento}`, dadosAtualizados, {
                headers: {
                    Authorization: autorizacao,
                    "Content-Type": "application/json",
                },
            })

            setDetalhesConversa(dadosAtualizados)
            enqueueSnackbar(
                `Resposta automática ${dadosAtualizados.automatica ? "habilitada" : "desabilitada"}`,
                { variant: "success" }
            )
        } catch (error) {
            console.error("Erro ao atualizar mensagens automáticas:", error)
            enqueueSnackbar("Erro ao atualizar configuração", { variant: "error" })
        }
    }

    const buscarMensagensContato = async (contato) => {
        if (!contato) return []

        const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id
        const autorizacao = localStorage.getItem("chaveSistema")

        if (!idEstabelecimento || !autorizacao) {
            return []
        }

        try {
            setCarregandoMensagens(true)
            const response = await api.get(`meta/mensagens/${idEstabelecimento}/${contato}`, {
                headers: {
                    Authorization: autorizacao,
                    "Content-Type": "application/json",
                },
            })

            if (response.data && Array.isArray(response.data)) {
                // Ordenar mensagens por ID (mais antigas primeiro)
                const mensagensOrdenadas = response.data.sort((a, b) => (a.id || 0) - (b.id || 0))
                return mensagensOrdenadas
            }
            return []
        } catch (error) {
            console.error("Erro ao buscar mensagens do contato:", error)
            enqueueSnackbar("Erro ao carregar mensagens da conversa", { variant: "error" })
            return []
        } finally {
            setCarregandoMensagens(false)
        }
    }

    const enviarMensagem = async () => {
        const contato = conversaSelecionada?.telefone
        const texto = (textoMensagem || "").trim()

        if (!contato || !texto) {
            enqueueSnackbar("Digite uma mensagem para enviar", { variant: "warning" })
            return
        }

        const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id
        const autorizacao = localStorage.getItem("chaveSistema")

        if (!idEstabelecimento || !autorizacao) {
            enqueueSnackbar("Não foi possível enviar. Faça login novamente.", {
                variant: "error",
            })
            return
        }

        setEnviando(true)
        try {
            await api.post(
                `meta/mensagem/${idEstabelecimento}`,
                { contato, texto },
                {
                    headers: {
                        Authorization: autorizacao,
                        "Content-Type": "application/json",
                    },
                }
            )
            enqueueSnackbar("Mensagem enviada com sucesso", { variant: "success" })
            setTextoMensagem("")

            // Mostrar mensagem imediatamente na tela (atualização otimista)
            const mensagemOtimista = {
                id: "temp-" + Date.now(),
                texto: texto,
                direcao: "OUT",
                dataEnvio: Date.now(),
                contato: contato,
            }
            setConversaSelecionada(prev => ({
                ...prev,
                mensagens: [...(prev?.mensagens || []), mensagemOtimista],
            }))

            // Scroll para o final imediatamente
            setTimeout(() => {
                if (messagesAreaRef.current) {
                    messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight
                }
            }, 50)

            // Atualizar lista de conversas e mensagens em background
            setPage(1)
            setHasMore(true)
            buscarConversas(false, 1, false).then(novasConversas => {
                if (conversaSelecionada?.telefone) {
                    buscarMensagensContato(conversaSelecionada.telefone).then(mensagensAtualizadas => {
                        if (mensagensAtualizadas.length > 0) {
                            setConversaSelecionada(prev => ({
                                ...prev,
                                mensagens: mensagensAtualizadas,
                            }))
                            setTimeout(() => {
                                if (messagesAreaRef.current) {
                                    messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight
                                }
                            }, 100)
                        }
                    })
                }
                if (novasConversas?.length && conversaSelecionada) {
                    const atualizada = novasConversas.find(
                        c => c.telefone === conversaSelecionada.telefone
                    )
                    if (atualizada) {
                        setConversaSelecionada(prev => ({
                            ...prev,
                            ...atualizada,
                            mensagens: prev?.mensagens || atualizada.mensagens,
                        }))
                    }
                }
            })
        } catch (error) {
            const status = error?.response?.status
            const mensagemErro = error?.response?.data?.mensagem || error?.response?.data

            if (status === 400) {
                enqueueSnackbar(MENSAGEM_ERRO_24H, { variant: "error" })
            } else {
                enqueueSnackbar(
                    typeof mensagemErro === "string" ? mensagemErro : "Erro ao enviar mensagem",
                    { variant: "error" }
                )
            }
        } finally {
            setEnviando(false)
        }
    }

    const buscarConversas = async (mostrarLoading = true, pagina = 1, append = false) => {
            try {
                if (mostrarLoading && !append) {
                    setLoading(true)
                } else if (append) {
                    setLoadingMore(true)
                }

                const idEstabelecimento =
                    configuracoes?.user?.parametros?.estabelecimento?.id

                if (!idEstabelecimento) {
                    enqueueSnackbar("Estabelecimento não encontrado", { variant: "error" })
                    if (mostrarLoading && !append) setLoading(false)
                    if (append) setLoadingMore(false)
                    return []
                }

                const autorizacao = localStorage.getItem("chaveSistema")

                if (!autorizacao) {
                    enqueueSnackbar("Autorização não encontrada", { variant: "error" })
                    if (mostrarLoading && !append) setLoading(false)
                    if (append) setLoadingMore(false)
                    return []
                }

                const size = 20 // Tamanho da página
                const response = await api.get(`meta/mensagens/${idEstabelecimento}?page=${pagina}&size=${size}`, {
                    headers: {
                        Authorization: autorizacao,
                        "Content-Type": "application/json",
                    },
                })

                if (response.data && Array.isArray(response.data)) {
                    // Se não houver dados, não há mais páginas
                    if (response.data.length === 0) {
                        setHasMore(false)
                        if (mostrarLoading && !append) setLoading(false)
                        if (append) setLoadingMore(false)
                        return append ? conversas : []
                    }

                    // Agrupar mensagens por contato
                    const conversasAgrupadas = {}

                    response.data.forEach(mensagem => {
                        const contato = mensagem.contato || mensagem.whatsapp

                        if (!conversasAgrupadas[contato]) {
                            conversasAgrupadas[contato] = {
                                contato: contato,
                                nome: mensagem.nome || formatarTelefone(contato),
                                mensagens: [],
                                ultimaMensagem: null,
                                ultimaDataEnvio: 0,
                                ultimoId: 0,
                            }
                        }

                        conversasAgrupadas[contato].mensagens.push(mensagem)

                        // Atualizar última mensagem se for mais recente
                        // Usa dataEnvio se disponível, senão usa o ID como fallback
                        const dataEnvio = mensagem.dataEnvio || 0
                        const mensagemId = mensagem.id || 0
                        
                        if (dataEnvio > 0) {
                            if (dataEnvio > conversasAgrupadas[contato].ultimaDataEnvio) {
                                conversasAgrupadas[contato].ultimaDataEnvio = dataEnvio
                                conversasAgrupadas[contato].ultimaMensagem = mensagem
                                conversasAgrupadas[contato].ultimoId = mensagemId
                            }
                        } else {
                            // Se dataEnvio for 0, usa o ID mais alto como referência
                            if (mensagemId > conversasAgrupadas[contato].ultimoId) {
                                conversasAgrupadas[contato].ultimaMensagem = mensagem
                                conversasAgrupadas[contato].ultimoId = mensagemId
                            }
                        }
                    })

                    // Converter para array e ordenar por data (mais recente primeiro)
                    const conversasArray = Object.values(conversasAgrupadas)
                        .map(conversa => {
                            // Contar mensagens não lidas (mensagens IN que ainda não foram visualizadas)
                            const naoLidas = conversa.mensagens.filter(
                                m => m.direcao === "IN" && m.status !== "read"
                            ).length

                            return {
                                id: conversa.contato,
                                nome: conversa.nome,
                                telefone: conversa.contato,
                                ultimaMensagem: conversa.ultimaMensagem?.texto || "",
                                ultimaDataEnvio: conversa.ultimaDataEnvio,
                                timestamp: formatarTimestamp(conversa.ultimaDataEnvio),
                                naoLidas: naoLidas,
                                mensagens: conversa.mensagens,
                            }
                        })
                        .sort((a, b) => {
                            const conversaA = conversasAgrupadas[a.telefone]
                            const conversaB = conversasAgrupadas[b.telefone]
                            
                            // Ordena por dataEnvio se disponível, senão por ID
                            if (conversaA.ultimaDataEnvio > 0 && conversaB.ultimaDataEnvio > 0) {
                                return conversaB.ultimaDataEnvio - conversaA.ultimaDataEnvio
                            } else {
                                return conversaB.ultimoId - conversaA.ultimoId
                            }
                        })

                    // Se for append, adiciona às conversas existentes, senão substitui
                    if (append) {
                        setConversas(prev => {
                            // Evitar duplicatas
                            const telefonesExistentes = new Set(prev.map(c => c.telefone))
                            const novasConversas = conversasArray.filter(
                                c => !telefonesExistentes.has(c.telefone)
                            )
                            return [...prev, ...novasConversas]
                        })
                    } else {
                        const prev = conversasRef.current
                        const listMudou =
                            prev.length !== conversasArray.length ||
                            conversasArray.some((c, i) => {
                                const p = prev[i]
                                if (!p || p.telefone !== c.telefone) return true
                                return (
                                    p.ultimaMensagem !== c.ultimaMensagem ||
                                    p.naoLidas !== c.naoLidas ||
                                    p.timestamp !== c.timestamp
                                )
                            })
                        setConversas(listMudou ? conversasArray : prev)
                        if (listMudou) {
                            setPage(1)
                            setHasMore(true)
                        }
                    }

                    // Se retornou menos que o tamanho da página, não há mais dados
                    if (response.data.length < size) {
                        setHasMore(false)
                    } else {
                        setHasMore(true)
                    }

                    if (mostrarLoading && !append) setLoading(false)
                    if (append) setLoadingMore(false)
                    return conversasArray
                } else {
                    setHasMore(false)
                    if (mostrarLoading && !append) setLoading(false)
                    if (append) setLoadingMore(false)
                    if (!append) setConversas([])
                    return []
                }
            } catch (error) {
                console.error("Erro ao buscar conversas:", error)
                enqueueSnackbar("Erro ao carregar conversas do WhatsApp", {
                    variant: "error",
                })
                setHasMore(false)
                if (mostrarLoading && !append) setLoading(false)
                if (append) setLoadingMore(false)
                if (!append) setConversas([])
                return []
            } finally {
                if (mostrarLoading && !append) setLoading(false)
                if (append) setLoadingMore(false)
            }
    }

    const atualizarConversas = async () => {
        setAtualizando(true)
        setTempoRestante(60)
        setPage(1)
        setHasMore(true)
        await buscarConversas(false, 1, false)
        setAtualizando(false)
    }

    const carregarMaisConversas = async () => {
        if (loadingMore || !hasMore) return

        const proximaPage = page + 1
        setPage(proximaPage)
        await buscarConversas(false, proximaPage, true)
    }

    useEffect(() => {
        conversasRef.current = conversas
    }, [conversas])

    useEffect(() => {
        setPage(1)
        setHasMore(true)
        buscarConversas(true, 1, false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuracoes, enqueueSnackbar])

    // Conectar WebSocket quando o componente montar
    useEffect(() => {
        const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id
        const autorizacao = localStorage.getItem("chaveSistema")

        if (!idEstabelecimento || !autorizacao) return

        // Obter URL base da API (alinhado ao axios: teste, amplify → pré-prod, senão produção)
        const urlBase = getRestApiBaseUrl().replace(/\/+$/, "")
        // Converter https:// para wss:// ou http:// para ws://
        const wsUrl = urlBase.replace(/^https?:\/\//, "").replace(/\/rest\/?$/, "")
        const protocol = urlBase.startsWith("https") ? "wss" : "ws"
        
        // Construir URL do WebSocket
        // Extrair token do header Authorization (remover "Basic " ou "Bearer ")
        const token = autorizacao.replace(/^(Basic|Bearer)\s+/i, "")
        const wsEndpoint = `${protocol}://${wsUrl}/rest/ws/whatsapp/${idEstabelecimento}?token=${encodeURIComponent(token)}`

        console.log("Conectando WebSocket:", wsEndpoint)

        try {
            const ws = new WebSocket(wsEndpoint)
            wsRef.current = ws

            ws.onopen = () => {
                console.log("WebSocket conectado")
                setWsConnected(true)
            }

            ws.onmessage = event => {
                try {
                    const data = JSON.parse(event.data)
                    console.log("Mensagem WebSocket recebida:", data)

                    if (data.type === "nova_mensagem" || data.type === "nova_mensagem_contato") {
                        // Se houver conversa selecionada e for do mesmo contato, atualizar mensagens
                        if (
                            conversaSelecionada?.telefone &&
                            data.contato === conversaSelecionada.telefone
                        ) {
                            buscarMensagensContato(conversaSelecionada.telefone).then(mensagens => {
                                if (mensagens.length > 0) {
                                    setConversaSelecionada({
                                        ...conversaSelecionada,
                                        mensagens: mensagens,
                                    })
                                    // Scroll para o final
                                    setTimeout(() => {
                                        if (messagesAreaRef.current) {
                                            messagesAreaRef.current.scrollTop =
                                                messagesAreaRef.current.scrollHeight
                                        }
                                    }, 100)
                                }
                            })
                        }

                        // Atualizar lista de conversas
                        buscarConversas(false, 1, false).then(() => {
                            // Se estava na primeira página, substituir
                            // Se estava em outras páginas, pode manter ou resetar
                            if (page === 1) {
                                // Já foi atualizado acima
                            }
                        })
                    } else if (data.type === "pong") {
                        // Resposta de ping, conexão está viva
                        console.log("Pong recebido")
                    }
                } catch (error) {
                    console.error("Erro ao processar mensagem WebSocket:", error)
                }
            }

            ws.onerror = error => {
                console.error("Erro no WebSocket:", error)
                setWsConnected(false)
            }

            ws.onclose = () => {
                console.log("WebSocket desconectado")
                setWsConnected(false)
                
                // Tentar reconectar após 5 segundos
                setTimeout(() => {
                    if (wsRef.current?.readyState === WebSocket.CLOSED) {
                        console.log("Tentando reconectar WebSocket...")
                        // Recriar conexão (o useEffect vai executar novamente)
                    }
                }, 5000)
            }

            // Enviar ping a cada 30 segundos para manter conexão viva
            const pingInterval = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send("ping")
                }
            }, 30000)

            return () => {
                clearInterval(pingInterval)
                if (wsRef.current) {
                    wsRef.current.close()
                    wsRef.current = null
                }
            }
        } catch (error) {
            console.error("Erro ao criar WebSocket:", error)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configuracoes?.user?.parametros?.estabelecimento?.id])

    // Atualização automática a cada 60 segundos (lista de conversas)
    useEffect(() => {
        const intervalo = setInterval(() => {
            atualizarConversas()
        }, 60000) // 60 segundos

        return () => clearInterval(intervalo)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Contador regressivo
    useEffect(() => {
        if (tempoRestante <= 0) {
            setTempoRestante(60)
            return
        }

        const timer = setInterval(() => {
            setTempoRestante(prev => {
                if (prev <= 1) {
                    return 60
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(timer)
    }, [tempoRestante])

    // Buscar mensagens e detalhes quando uma conversa é selecionada
    useEffect(() => {
        if (conversaSelecionada?.telefone) {
            // Buscar detalhes da conversa
            buscarDetalhesConversa(conversaSelecionada.telefone).then(detalhes => {
                if (detalhes) {
                    setDetalhesConversa(detalhes)
                }
            })

            // Buscar mensagens da conversa
            buscarMensagensContato(conversaSelecionada.telefone).then(mensagens => {
                if (mensagens.length > 0) {
                    setConversaSelecionada({
                        ...conversaSelecionada,
                        mensagens: mensagens,
                    })
                    // Scroll para o final após carregar mensagens
                    setTimeout(() => {
                        if (messagesAreaRef.current) {
                            messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight
                        }
                    }, 100)
                }
            })
        } else {
            setDetalhesConversa(null)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversaSelecionada?.telefone])

    // Scroll automático quando novas mensagens são adicionadas
    useEffect(() => {
        if (conversaSelecionada?.mensagens?.length > 0 && messagesAreaRef.current) {
            setTimeout(() => {
                if (messagesAreaRef.current) {
                    messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight
                }
            }, 100)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversaSelecionada?.mensagens?.length])

    // Ao entrar na tela, rolar a lista de conversas para o topo (última conversa = mais recente primeiro)
    useEffect(() => {
        if (!loading && conversas.length > 0 && conversationsListRef.current) {
            conversationsListRef.current.scrollTop = 0
        }
    }, [loading, conversas.length])

    // Detectar se o drawer está aberto através do DOM (solução temporária)
    const [drawerOpen, setDrawerOpen] = useState(false)

    useEffect(() => {
        const checkDrawer = () => {
            const drawer = document.querySelector('[class*="drawerOpen"]')
            const isOpen = drawer && drawer.offsetWidth > 100
            setDrawerOpen(isOpen)
        }

        // Verificar inicialmente
        checkDrawer()

        // Observar mudanças no drawer
        const observer = new MutationObserver(checkDrawer)
        const drawerElement = document.querySelector('[class*="drawer"]')
        if (drawerElement) {
            observer.observe(drawerElement, {
                attributes: true,
                attributeFilter: ['class'],
            })
        }

        // Verificar periodicamente também
        const interval = setInterval(checkDrawer, 500)

        return () => {
            observer.disconnect()
            clearInterval(interval)
        }
    }, [])

    return (
        <div className={clsx(classes.root, { [classes.rootShift]: drawerOpen })}>
            <Cabecalho pagina={"Conversas do Whatsapp"} />
            <Paper className={classes.header}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h5" className={classes.title}>
                            <Chat />
                            Conversas do Whatsapp
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                            Gerencie suas conversas e mensagens do WhatsApp
                        </Typography>
                    </Box>
                    <Box className={classes.headerActions}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<GetApp />}
                            onClick={handleExportarContatos}
                            disabled={conversas.length === 0 || loading}
                            style={{ marginRight: 16 }}
                        >
                            Exportar Contatos
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={handleAbrirDisparoMassa}
                            disabled={conversasSelecionadas.length === 0}
                            style={{ marginRight: 16 }}
                        >
                            Disparo em Massa
                            {conversasSelecionadas.length > 0 && ` (${conversasSelecionadas.length})`}
                        </Button>
                        <Box className={classes.refreshButton}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={atualizando ? <CircularProgress size={16} /> : <Refresh />}
                                onClick={atualizarConversas}
                                disabled={atualizando}
                            >
                                Atualizar
                            </Button>
                            <Typography className={classes.contador}>
                                Próxima atualização em: {tempoRestante}s
                            </Typography>
                        </Box>
                        {wsConnected && (
                            <Chip
                                label="WebSocket Conectado"
                                size="small"
                                color="primary"
                                style={{ marginLeft: 8 }}
                            />
                        )}
                    </Box>
                </Box>
            </Paper>

            <Box className={classes.container}>
                {/* Painel de conversas à esquerda */}
                <Box className={classes.conversationsPanel}>
                    <Box className={classes.conversationPanelHeader}>
                        <Typography variant="h6">Conversas</Typography>
                    </Box>
                    {loading ? (
                        <Box className={classes.loadingContainer}>
                            <CircularProgress />
                        </Box>
                    ) : conversas.length === 0 ? (
                        <Box className={classes.emptyState}>
                            <Chat style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
                            <Typography variant="h6" color="textSecondary">
                                Nenhuma conversa encontrada
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                As conversas do WhatsApp aparecerão aqui quando disponíveis
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            className={classes.conversationsListContainer}
                            ref={conversationsListRef}
                            onScroll={e => {
                                const container = e.target
                                const { scrollTop, scrollHeight, clientHeight } = container
                                // Quando estiver próximo do final (50px do fim)
                                if (
                                    scrollHeight - scrollTop - clientHeight < 50 &&
                                    hasMore &&
                                    !loadingMore &&
                                    !loading
                                ) {
                                    carregarMaisConversas()
                                }
                            }}
                        >
                            <List>
                                {conversas.map(conversa => {
                                    const podeSelecionar = conversaTemMensagemUltimas24h(conversa)
                                    const estaSelecionada = conversasSelecionadas.includes(conversa.telefone)

                                    return (
                                        <ListItem
                                            key={conversa.id}
                                            className={`${classes.conversationItem} ${
                                                conversaSelecionada?.telefone === conversa.telefone
                                                    ? "selected"
                                                    : ""
                                            } ${!podeSelecionar ? "disabled" : ""}`}
                                            onClick={e => {
                                                // Não selecionar conversa se clicar no checkbox
                                                if (e.target.type === "checkbox") return
                                                setConversaSelecionada(conversa)
                                                setTextoMensagem("")
                                            }}
                                            style={{
                                                opacity: podeSelecionar ? 1 : 0.5,
                                                cursor: podeSelecionar ? "pointer" : "not-allowed",
                                            }}
                                        >
                                            {podeSelecionar && (
                                                <ListItemIcon>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={estaSelecionada}
                                                        onClick={e => {
                                                            e.stopPropagation()
                                                            toggleSelecaoConversa(conversa.telefone)
                                                        }}
                                                        inputProps={{ "aria-labelledby": `checkbox-${conversa.id}` }}
                                                    />
                                                </ListItemIcon>
                                            )}
                                            <ListItemAvatar>
                                                <Avatar className={classes.avatar}>
                                                    {conversa.nome && conversa.nome.length > 0
                                                        ? conversa.nome.charAt(0).toUpperCase()
                                                        : conversa.telefone.charAt(0)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                            primary={
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                >
                                                    <Typography variant="subtitle1">
                                                        {conversa.nome ||
                                                            formatarTelefone(conversa.telefone)}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        className={classes.timestamp}
                                                    >
                                                        {conversa.timestamp}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Box
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    style={{ marginTop: 4 }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        className={classes.messagePreview}
                                                    >
                                                        {conversa.ultimaMensagem || "Sem mensagens"}
                                                    </Typography>
                                                    {conversa.naoLidas > 0 && (
                                                        <Chip
                                                            label={conversa.naoLidas}
                                                            size="small"
                                                            className={classes.unreadBadge}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                )
                                })}
                            </List>
                            {loadingMore && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    padding={2}
                                >
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                            {!hasMore && conversas.length > 0 && (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    padding={2}
                                >
                                    <Typography variant="caption" color="textSecondary">
                                        Todas as conversas foram carregadas
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Painel de chat à direita */}
                <Box className={classes.chatPanel}>
                    {conversaSelecionada ? (
                        <>
                            <Box className={classes.chatHeader}>
                                <Box className={classes.chatHeaderTop}>
                                    <Box className={classes.chatHeaderInfo}>
                                        <Avatar className={classes.avatar} style={{ width: 40, height: 40 }}>
                                            {conversaSelecionada.nome && conversaSelecionada.nome.length > 0
                                                ? conversaSelecionada.nome.charAt(0).toUpperCase()
                                                : conversaSelecionada.telefone.charAt(0)}
                                        </Avatar>
                                        <Box>
                                            <Typography variant="h6">
                                                {detalhesConversa?.nome ||
                                                    conversaSelecionada.nome ||
                                                    formatarTelefone(conversaSelecionada.telefone)}
                                            </Typography>
                                            {carregandoDetalhes && (
                                                <CircularProgress size={16} style={{ marginTop: 4 }} />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                {detalhesConversa && (
                                    <Box className={classes.chatHeaderDetails}>
                                        <Typography variant="body2">
                                            Pedidos: {detalhesConversa.quantidadePedidos || 0}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={toggleMensagensAutomaticas}
                                            className={`${classes.automaticaButton} ${
                                                detalhesConversa.automatica
                                                    ? classes.automaticaButtonEnabled
                                                    : classes.automaticaButtonDisabled
                                            }`}
                                        >
                                            {detalhesConversa.automatica
                                                ? "Mensagens Automáticas Habilitadas"
                                                : "Mensagens Automáticas Desabilitadas"}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                            <Box className={classes.messagesArea} ref={messagesAreaRef}>
                                {carregandoMensagens ? (
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        style={{ minHeight: 200 }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                ) : conversaSelecionada?.mensagens?.length > 0 ? (
                                    <Box display="flex" flexDirection="column">
                                        {conversaSelecionada.mensagens
                                            .slice()
                                            .sort((a, b) => (a.id || 0) - (b.id || 0))
                                            .map(msg => (
                                                <Box
                                                    key={msg.id || msg.wpid || Math.random()}
                                                    className={`${classes.messageBubble} ${
                                                        msg.direcao === "IN"
                                                            ? classes.messageIn
                                                            : classes.messageOut
                                                    }`}
                                                >
                                                    <Typography variant="body2" className={classes.messageText}>
                                                        {msg.texto || ""}
                                                    </Typography>
                                                    <Typography variant="caption" className={classes.messageTime}>
                                                        {formatarDataMensagem(msg.dataEnvio)}
                                                    </Typography>
                                                </Box>
                                            ))}
                                    </Box>
                                ) : (
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        style={{ minHeight: 200, color: "#999" }}
                                    >
                                        <Typography variant="body2">
                                            Nenhuma mensagem encontrada
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            {!conversaTemMensagemUltimas24h(conversaSelecionada) ? (
                                <Box className={classes.sendArea} style={{ justifyContent: "center", backgroundColor: "#fff3e0" }}>
                                    <Typography variant="body2" color="textSecondary" style={{ padding: "8px 16px" }}>
                                        Conversa fora da janela permitida para respostas pelo sistema
                                    </Typography>
                                </Box>
                            ) : (
                            <Box className={classes.sendArea}>
                                <IconButton
                                    size="small"
                                    onClick={e => setEmojiPickerAnchor(e.currentTarget)}
                                    disabled={enviando}
                                    title="Emojis"
                                >
                                    <InsertEmoticon />
                                </IconButton>
                                <TextField
                                    className={classes.sendInput}
                                    placeholder="Digite sua mensagem..."
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    maxRows={4}
                                    value={textoMensagem}
                                    onChange={e => setTextoMensagem(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault()
                                            enviarMensagem()
                                        }
                                    }}
                                    disabled={enviando}
                                />
                                <IconButton
                                    color="primary"
                                    onClick={enviarMensagem}
                                    disabled={enviando || !textoMensagem.trim()}
                                >
                                    {enviando ? <CircularProgress size={24} /> : <Send />}
                                </IconButton>
                                <Popover
                                    open={Boolean(emojiPickerAnchor)}
                                    anchorEl={emojiPickerAnchor}
                                    onClose={() => setEmojiPickerAnchor(null)}
                                    anchorOrigin={{ vertical: "top", horizontal: "left" }}
                                    transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                                >
                                    <Box p={1.5} style={{ maxWidth: 320 }}>
                                        <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: "block" }}>
                                            Escolha um emoji
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                                            {EMOJIS_COMUNS.map((emoji, i) => (
                                                <IconButton
                                                    key={i}
                                                    size="small"
                                                    onClick={() => {
                                                        setTextoMensagem(prev => (prev || "") + emoji)
                                                        setEmojiPickerAnchor(null)
                                                    }}
                                                    style={{ fontSize: "1.25rem" }}
                                                >
                                                    {emoji}
                                                </IconButton>
                                            ))}
                                        </Box>
                                    </Box>
                                </Popover>
                            </Box>
                            )}
                        </>
                    ) : (
                        <Box className={classes.emptyChatState}>
                            <Chat style={{ fontSize: 120, color: "#ccc", marginBottom: 16 }} />
                            <Typography variant="h6" color="textSecondary">
                                Selecione uma conversa
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Escolha uma conversa da lista para começar a conversar
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Dialog para Disparo em Massa */}
            <Dialog
                open={openDisparoMassa}
                onClose={() => !enviandoMassa && setOpenDisparoMassa(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Disparo em Massa</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                        Enviando para {conversasSelecionadas.length}{" "}
                        {conversasSelecionadas.length === 1 ? "conversa selecionada" : "conversas selecionadas"}
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Mensagem"
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        value={textoDisparoMassa}
                        onChange={e => setTextoDisparoMassa(e.target.value)}
                        placeholder="Digite a mensagem que será enviada para todos os contatos selecionados..."
                        disabled={enviandoMassa}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setOpenDisparoMassa(false)
                            setTextoDisparoMassa("")
                        }}
                        disabled={enviandoMassa}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleEnviarDisparoMassa}
                        variant="contained"
                        color="primary"
                        disabled={enviandoMassa || !textoDisparoMassa.trim()}
                        startIcon={enviandoMassa ? <CircularProgress size={20} /> : <Send />}
                    >
                        {enviandoMassa ? "Enviando..." : "Enviar"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}
